import { createLazyModuleLoader } from './lazyModuleLoader.js';
import {
  buildBookshelfInteractionsInitContext,
  buildAppEventBindingsInitContext
} from './moduleInitContextBuilders.js';
import { ensureModuleWithInit } from './moduleRuntimeHelpers.js';

export function createCoreUiRuntime(context = {}) {
  const {
    importWithChunkRecovery,
    state,
    elements,
    getThoughtsCallbacks,
    getRenderThoughtsView,
    openModal,
    closeModal,
    showToast,
    addThought,
    updateThought,
    formatDate,
    addEssay,
    updateEssay,
    deleteEssay,
    getActiveCategoryEditMenuCleanup,
    clearActiveCategoryEditMenuCleanup,
    deleteBook,
    removeBooksFromAllCollections,
    removeBooksFromAllBooklists,
    renderBookshelf,
    showEditCollectionMenu,
    showAddCollectionModal,
    showAllCollectionsModal,
    showBookDetail,
    toggleBookSelection,
    openBook,
    enterSelectMode,
    saveCollections,
    saveGlobalShelfOrder,
    renderCollectionFilters,
    updateBooksOrder,
    switchView,
    setupBookshelfDelegatedEvents,
    exitSelectMode,
    setViewMode,
    renderBooklistView,
    syncUnifiedSearchUi,
    handlePrimaryActionClick,
    handleFileSelect,
    toggleSiteTheme,
    setupSiteThemeLongPressFullscreen,
    toggleSortMenu,
    handleSort,
    updateWelcomeMessage,
    handleSearch,
    clearSearch,
    openThoughtModal,
    createNewEssay,
    deleteCurrentEssay,
    handleEssayTitleChange,
    toggleEssaysSidebar,
    closeReader,
    navigatePage,
    handleHighlight,
    openQuickThought,
    toggleToc,
    setupSelectionToolbar,
    ensureReaderDisplayModeModuleReady,
    ensureReaderUiPanelsModuleReady,
    ensureReaderRenderSizingModuleReady,
    ensureReaderQuickThoughtModuleReady,
    handleTocSearch,
    clearTocSearch,
    togglePanel,
    toggleFullscreen,
    changeFontSize,
    changeLineHeight,
    changeFontFamily,
    changeTheme,
    setupReaderOverlayOutsideCloseEvents,
    ensureReaderKeyboardModuleReady,
    handleKeyboard,
    handleDrop,
    deleteSelectedBooks,
    showBatchCategoryModal,
    showBatchCollectionModal,
    bindAiLazyEntrypoints,
    setupEnhancedReaderEvents,
    syncThoughtsScrollbarGutter
  } = context;

  let thoughtsModuleApi = null;
  let thoughtsModuleLoadingPromise = null;
  let thoughtsModuleEventsBound = false;
  let thoughtModalModuleApi = null;
  let thoughtModalModuleLoadingPromise = null;
  let essaysEditorModuleApi = null;
  let essaysEditorModuleLoadingPromise = null;
  let modalCoreModuleApi = null;
  let modalCoreModuleLoadingPromise = null;
  let bookDeleteModalModuleApi = null;
  let bookDeleteModalModuleLoadingPromise = null;
  let mapLocationBridgeModuleApi = null;
  let mapLocationBridgeModuleLoadingPromise = null;
  let thoughtsMobileSidebarModuleApi = null;
  let thoughtsMobileSidebarModuleLoadingPromise = null;
  let bookshelfInteractionsModuleApi = null;
  let bookshelfInteractionsModuleLoadingPromise = null;
  let appEventBindingsModuleApi = null;
  let appEventBindingsModuleLoadingPromise = null;

  const loadThoughtsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./thoughtsModule.js'),
    getApi: () => thoughtsModuleApi,
    setApi: (mod) => {
      thoughtsModuleApi = mod;
    },
    getLoadingPromise: () => thoughtsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      thoughtsModuleLoadingPromise = promise;
    }
  });

  const loadThoughtModalModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./thoughtModalModule.js'),
    getApi: () => thoughtModalModuleApi,
    setApi: (mod) => {
      thoughtModalModuleApi = mod;
    },
    getLoadingPromise: () => thoughtModalModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      thoughtModalModuleLoadingPromise = promise;
    }
  });

  function ensureThoughtModalModuleReady() {
    return ensureModuleWithInit(loadThoughtModalModule, {
      isInitializedExport: 'isThoughtModalModuleInitialized',
      initExport: 'initThoughtModalModule',
      createInitContext: () => ({
        state,
        elements,
        openModal,
        closeModal,
        showToast,
        addThought,
        updateThought,
        getRenderThoughtsView
      })
    });
  }

  const loadEssaysEditorModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./essaysEditorModule.js'),
    getApi: () => essaysEditorModuleApi,
    setApi: (mod) => {
      essaysEditorModuleApi = mod;
    },
    getLoadingPromise: () => essaysEditorModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      essaysEditorModuleLoadingPromise = promise;
    }
  });

  function ensureEssaysEditorModuleReady() {
    return ensureModuleWithInit(loadEssaysEditorModule, {
      isInitializedExport: 'isEssaysEditorModuleInitialized',
      initExport: 'initEssaysEditorModule',
      createInitContext: () => ({
        state,
        elements,
        showToast,
        formatDate,
        importWithChunkRecovery,
        addEssay,
        updateEssay,
        deleteEssay
      })
    });
  }

  const loadModalCoreModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./modalCoreModule.js'),
    getApi: () => modalCoreModuleApi,
    setApi: (mod) => {
      modalCoreModuleApi = mod;
    },
    getLoadingPromise: () => modalCoreModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      modalCoreModuleLoadingPromise = promise;
    }
  });

  function ensureModalCoreModuleReady() {
    return ensureModuleWithInit(loadModalCoreModule, {
      isInitializedExport: 'isModalCoreModuleInitialized',
      initExport: 'initModalCoreModule',
      createInitContext: () => ({
        elements,
        syncModalOverlayTheme: context.syncModalOverlayTheme,
        getActiveCategoryEditMenuCleanup,
        clearActiveCategoryEditMenuCleanup
      })
    });
  }

  const loadBookDeleteModalModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./bookDeleteModalModule.js'),
    getApi: () => bookDeleteModalModuleApi,
    setApi: (mod) => {
      bookDeleteModalModuleApi = mod;
    },
    getLoadingPromise: () => bookDeleteModalModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      bookDeleteModalModuleLoadingPromise = promise;
    }
  });

  function ensureBookDeleteModalModuleReady() {
    return ensureModuleWithInit(loadBookDeleteModalModule, {
      isInitializedExport: 'isBookDeleteModalModuleInitialized',
      initExport: 'initBookDeleteModalModule',
      createInitContext: () => ({
        state,
        elements,
        openModal,
        closeModal,
        showToast,
        deleteBook,
        removeBooksFromAllCollections,
        removeBooksFromAllBooklists,
        renderBookshelf
      })
    });
  }

  const loadMapLocationBridgeModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./mapLocationBridgeModule.js'),
    getApi: () => mapLocationBridgeModuleApi,
    setApi: (mod) => {
      mapLocationBridgeModuleApi = mod;
    },
    getLoadingPromise: () => mapLocationBridgeModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      mapLocationBridgeModuleLoadingPromise = promise;
    }
  });

  function ensureMapLocationBridgeModuleReady() {
    return ensureModuleWithInit(loadMapLocationBridgeModule, {
      isInitializedExport: 'isMapLocationBridgeModuleInitialized',
      initExport: 'initMapLocationBridgeModule'
    });
  }

  const loadThoughtsMobileSidebarModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./thoughtsMobileSidebarModule.js'),
    getApi: () => thoughtsMobileSidebarModuleApi,
    setApi: (mod) => {
      thoughtsMobileSidebarModuleApi = mod;
    },
    getLoadingPromise: () => thoughtsMobileSidebarModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      thoughtsMobileSidebarModuleLoadingPromise = promise;
    }
  });

  function ensureThoughtsMobileSidebarModuleReady() {
    return ensureModuleWithInit(loadThoughtsMobileSidebarModule, {
      isInitializedExport: 'isThoughtsMobileSidebarModuleInitialized',
      initExport: 'initThoughtsMobileSidebarModule'
    });
  }

  const loadBookshelfInteractionsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./bookshelfInteractionsModule.js'),
    getApi: () => bookshelfInteractionsModuleApi,
    setApi: (mod) => {
      bookshelfInteractionsModuleApi = mod;
    },
    getLoadingPromise: () => bookshelfInteractionsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      bookshelfInteractionsModuleLoadingPromise = promise;
    }
  });

  function ensureBookshelfInteractionsModuleReady() {
    return ensureModuleWithInit(loadBookshelfInteractionsModule, {
      isInitializedExport: 'isBookshelfInteractionsModuleInitialized',
      initExport: 'initBookshelfInteractionsModule',
      createInitContext: () => buildBookshelfInteractionsInitContext({
        state,
        elements,
        showEditCollectionMenu,
        showAddCollectionModal,
        showAllCollectionsModal,
        renderBookshelf,
        showBookDetail,
        toggleBookSelection,
        openBook,
        enterSelectMode,
        saveCollections,
        saveGlobalShelfOrder,
        renderCollectionFilters,
        updateBooksOrder,
        showToast
      })
    });
  }

  const loadAppEventBindingsModule = createLazyModuleLoader({
    importWithChunkRecovery,
    importer: () => import('./appEventBindingsModule.js'),
    getApi: () => appEventBindingsModuleApi,
    setApi: (mod) => {
      appEventBindingsModuleApi = mod;
    },
    getLoadingPromise: () => appEventBindingsModuleLoadingPromise,
    setLoadingPromise: (promise) => {
      appEventBindingsModuleLoadingPromise = promise;
    }
  });

  function ensureAppEventBindingsModuleReady() {
    return ensureModuleWithInit(loadAppEventBindingsModule, {
      isInitializedExport: 'isAppEventBindingsModuleInitialized',
      initExport: 'initAppEventBindingsModule',
      createInitContext: () => buildAppEventBindingsInitContext({
        elements,
        state,
        switchView,
        setupBookshelfDelegatedEvents,
        exitSelectMode,
        renderBookshelf,
        setViewMode,
        renderBooklistView,
        syncUnifiedSearchUi,
        handlePrimaryActionClick,
        handleFileSelect,
        toggleSiteTheme,
        setupSiteThemeLongPressFullscreen,
        toggleSortMenu,
        handleSort,
        updateWelcomeMessage,
        handleSearch,
        clearSearch,
        openThoughtModal,
        createNewEssay,
        deleteCurrentEssay,
        handleEssayTitleChange,
        toggleEssaysSidebar,
        closeReader,
        navigatePage,
        handleHighlight,
        openQuickThought,
        toggleToc,
        setupSelectionToolbar,
        ensureReaderDisplayModeModuleReady,
        ensureReaderUiPanelsModuleReady,
        ensureReaderRenderSizingModuleReady,
        ensureReaderQuickThoughtModuleReady,
        handleTocSearch,
        clearTocSearch,
        togglePanel,
        toggleFullscreen,
        changeFontSize,
        changeLineHeight,
        changeFontFamily,
        changeTheme,
        setupReaderOverlayOutsideCloseEvents,
        closeModal,
        ensureReaderKeyboardModuleReady,
        handleKeyboard,
        handleDrop,
        deleteSelectedBooks,
        showBatchCategoryModal,
        showBatchCollectionModal,
        bindAiLazyEntrypoints,
        setupEnhancedReaderEvents,
        syncThoughtsScrollbarGutter
      })
    });
  }

  async function ensureThoughtsModuleReady() {
    const mod = await loadThoughtsModule();
    if (!thoughtsModuleEventsBound && mod?.setupThoughtsEvents) {
      mod.setupThoughtsEvents(elements, state, {
        ...getThoughtsCallbacks(),
        renderThoughts: getRenderThoughtsView()
      });
      thoughtsModuleEventsBound = true;
    }
    return mod;
  }

  return {
    ensureThoughtsModuleReady,
    getThoughtsModuleApi: () => thoughtsModuleApi,
    isThoughtsModuleEventsBound: () => thoughtsModuleEventsBound,
    ensureThoughtModalModuleReady,
    getThoughtModalModuleApi: () => thoughtModalModuleApi,
    ensureEssaysEditorModuleReady,
    getEssaysEditorModuleApi: () => essaysEditorModuleApi,
    ensureModalCoreModuleReady,
    getModalCoreModuleApi: () => modalCoreModuleApi,
    ensureBookDeleteModalModuleReady,
    getBookDeleteModalModuleApi: () => bookDeleteModalModuleApi,
    ensureMapLocationBridgeModuleReady,
    ensureThoughtsMobileSidebarModuleReady,
    getThoughtsMobileSidebarModuleApi: () => thoughtsMobileSidebarModuleApi,
    ensureBookshelfInteractionsModuleReady,
    getBookshelfInteractionsModuleApi: () => bookshelfInteractionsModuleApi,
    ensureAppEventBindingsModuleReady,
    getAppEventBindingsModuleApi: () => appEventBindingsModuleApi
  };
}
